import axios from "axios";
import { get, writable } from "svelte/store";
import GeoHelper from "../Helpers/GeoHelper";

import LocalStorageHelper from "../Helpers/LocalStorageHelper";
import MapDataSource from "../Map/Enums/MapDataSource";
import MapLayer from "../Map/Enums/MapLayer";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import MapLayerManager from "../Map/MapLayerManager";
import { Paint } from "../Map/MapLayerStyles";
import MapStore from "../Map/Store";

const TimezoneLayer = {
	meta: {
		localStorageKey: "Geollect.TimezoneLayer"
	},
	data: GeoHelper.emptyGeoJson(),
	filename: "_timezone.json"
};

const TimezoneLabels = {
	meta: {
		localStorageKey: "Geollect.TimezoneLabels"
	},
	data: GeoHelper.emptyGeoJson(),
	filename: "_timezone_labels.json"
};

TimezoneLayer.CACHE_DEFAULT = "{ \"last_updated\": -1, \"geojson\": \"\" }";

const Timezones = {
	meta: {
		initialised: writable(false),
		lastUpdated: writable(Date.now()),
		mapInitted: writable(false)
	},
	layers: {
		[TimezoneLayer.meta.localStorageKey]: TimezoneLayer,
		[TimezoneLabels.meta.localStorageKey]: TimezoneLabels
	}
};
Timezones.initMap = () => {
	if (get(Timezones.meta.mapInitted)) {
		return;
	}

	const map = MapStore.helpers.getMap(); // get(MapStore.mapbox.instance);

	map.addSource(MapDataSource.TIMEZONE, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.TIMEZONE_LABELS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});

	map.addLayer({
		id: MapLayer.TIMEZONE_NAMES,
		type: "symbol",
		source: MapDataSource.TIMEZONE_LABELS,
		layout: {
			visibility: "none",
			"text-field": [
				"format",
				["upcase", ["get", "name"]],
				{ "font-scale": 1 }
			],
			"text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
			"text-anchor": "left"
			// "text-offset": [1.5, 0]
		}
	});

	map.addLayer(
		{
			id: MapLayer.TIMEZONE_LINES,
			type: "line",
			source: MapDataSource.TIMEZONE,
			layout: {
				visibility: "none"
			},
			paint: Paint.timezoneLines
		}, MapLayer.TIMEZONE_NAMES
	);

	map.addLayer(
		{
			id: MapLayer.TIMEZONE,
			type: "fill",
			source: MapDataSource.TIMEZONE,
			layout: {
				visibility: "none"
			},
			paint: Paint.timezone
		}, MapLayer.TIMEZONE_NAMES
	);

	MapLayerManager.addLayerGroup(MapLayerGroup.TIMEZONES, [
		MapLayer.TIMEZONE_NAMES,
		MapLayer.TIMEZONE_LINES,
		MapLayer.TIMEZONE
	], [
		MapDataSource.TIMEZONE,
		MapDataSource.TIMEZONE_LABELS
	]);

	Timezones.meta.mapInitted.set(true);
	Timezones.meta.lastUpdated.set(Date.now());
};
Timezones.deinitMap = () => {
	MapLayerManager.removeLayerGroup(MapLayerGroup.TIMEZONES);
	Timezones.meta.mapInitted.set(false);
};

Timezones.load = () => {
	const layersToLoad = Object.values(Timezones.layers);
	let countLoaded = 0;
	const onCountChanged = () => {
		if (countLoaded === layersToLoad.length) {
			Timezones.meta.initialised.set(true);
			Timezones.meta.lastUpdated.set(Date.now());
		}
	};

	layersToLoad.forEach((layer) => {
		let loadedLocal = false;
		if (LocalStorageHelper.has(layer.meta.localStorageKey)) {
			const data = JSON.parse(LocalStorageHelper.get(layer.meta.localStorageKey));
			if (data) {
				if (data.properties.version === __VERSION) {
					layer.data = data;
					loadedLocal = true;
					countLoaded++;
					onCountChanged();
				}
			}
		}

		if (!loadedLocal) {
			const fileUrl = window.location.origin + "/files/" + layer.filename;
			axios.get(fileUrl)
				.then(response => {
					return { success: (response.status === 200), data: response.data };
				})
				.then((response) => {
					if (!response.success) {
						console.warning("Timezones.load", response);
						return;
					}
					layer.data = response.data;
					layer.data.properties.version = __VERSION;
					LocalStorageHelper.set(layer.meta.localStorageKey, JSON.stringify(layer.data));
					countLoaded++;
					onCountChanged();
				})
				.catch(error => {
					console.error("Timezones.load", error);
				});
		}
	});
};

Timezones.get = (layerId) => {
	const key = "Geollect." + layerId;
	const cachedLayer = Timezones.layers[key];
	return cachedLayer.data;
};

export default Timezones;
